import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  type AccountsPeriodDto,
  type ChargesDto,
  type CompanyDetail,
  type CompanyHit,
  type ConsolidationDto,
  type DetailQuote,
  type EventsDto,
  type FieldEnvelope,
  type FilingsDto,
  type FinancialSummaryDto,
  type FundingRoundsDto,
  type GovernanceDto,
  type ShareCapitalDto,
  type ValuationDto,
} from "../lib/api";
import { companyDetailPath, companyUrl } from "../lib/companyUrl";
import { isDistressKind } from "../lib/eventSeverity";
import { formatMoney } from "../lib/money";
// Same generated taxonomy the Dashboard/Watchlists pages read labels from.
import { EVENT_LABELS as GEN_EVENT_LABELS } from "../../../../schemas/generated/ts/watch_events";

const EVENT_LABELS: Record<string, string> = GEN_EVENT_LABELS;

// ─── formatting helpers ────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtCount(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-GB").format(v);
}

// Full-precision money for figures where compact notation loses the point
// (price per share, nominal values).
function fmtMoneyExact(
  value: number | null | undefined,
  currency: string | null | undefined,
  maxFrac = 2,
): string {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency ?? "GBP",
      maximumFractionDigits: maxFrac,
    }).format(value);
  } catch {
    return new Intl.NumberFormat("en-GB", {
      maximumFractionDigits: maxFrac,
    }).format(value);
  }
}

function fmtPct(fraction: number | null | undefined, signed = true): string {
  if (fraction == null || !Number.isFinite(fraction)) return "—";
  const pct = fraction * 100;
  const sign = signed && pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

// A due date strictly before today reads as overdue. Estimated — the
// registry snapshot can lag and registers allow grace periods, hence the
// "(est.)" in the badge copy.
function isOverdue(due: string | null | undefined): boolean {
  if (!due) return false;
  return due < new Date().toISOString().slice(0, 10);
}

// Filing descriptions arrive as CH description slugs
// ("accounts-with-accounts-type-full") — humanise for display.
function humaniseSlug(s: string | null | undefined): string {
  if (!s) return "—";
  return s.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

// ─── envelope rendering ────────────────────────────────────────────────
//
// Every numeric arrives as a {value, source} envelope. The value renders
// with a provenance flag whenever it is NOT a plain as-reported figure —
// filing-derived/estimated values stay labelled (CLAUDE.md invariant 4).

const SOURCE_LABELS: Record<string, string> = {
  derived: "derived",
  pdf_heuristic: "parsed from PDF",
  ixbrl: "parsed from iXBRL",
};

function SourceFlag({ source }: { source: string | undefined }) {
  if (!source || source === "reported" || source === "unavailable") return null;
  const label = SOURCE_LABELS[source] ?? source;
  return (
    <sup className="src-flag" title={`${label} — filing-derived, not advice`}>
      {label === "derived" ? "d" : "p"}
    </sup>
  );
}

function Fig({
  label,
  field,
  fmt,
  extra,
}: {
  label: string;
  field: FieldEnvelope | undefined;
  fmt: (v: number) => string;
  extra?: React.ReactNode;
}) {
  const has = field && field.value != null && field.source !== "unavailable";
  return (
    <div className="fig">
      <div className="eyebrow">{label}</div>
      <div className="fig-value mono">
        {has ? fmt(field.value as number) : "—"}
        {has && <SourceFlag source={field.source} />}
        {extra}
      </div>
    </div>
  );
}

function YoY({ growth }: { growth: FieldEnvelope | undefined }) {
  if (!growth || growth.value == null || !Number.isFinite(growth.value)) {
    return null;
  }
  const up = growth.value > 0;
  return (
    <span
      className={`yoy ${up ? "up" : "down"}`}
      title="Year-on-year change vs the prior reported period (derived)"
    >
      {fmtPct(growth.value)} YoY
    </span>
  );
}

// ─── search (index route) ──────────────────────────────────────────────

function hitKey(h: CompanyHit): string {
  return `${h.entity_id}|${h.schema}|${h.jurisdiction ?? ""}`;
}

// Client-side cost ESTIMATE for a search hit, so the results list is
// legible before anyone clicks. Mirrors the worker's classifyRead
// (rate/logic.ts): coverage plans read watched companies free, metered
// (developer) plans pay per read, locked can't deep-read at all. The one
// input the SPA can't see is showcase membership (env config) — a
// showcase hit shows "1 credit" here and then simply loads free, because
// the /detail/quote gate stays the authority and errs are only ever in
// the pleasant direction. Never draws anything itself.
type HitCost = "free_watched" | "credit" | "blocked";

function estimateHitCost(
  readModel: string,
  watched: ReadonlySet<string>,
  entityId: string,
): HitCost {
  if (readModel === "locked") return "blocked";
  if (readModel === "coverage" && watched.has(entityId)) {
    return "free_watched";
  }
  return "credit";
}

function HitCostBadge({ cost }: { cost: HitCost }) {
  if (cost === "free_watched") {
    return (
      <span className="chip ok" title="On your watchlist — full data is always free to view">
        Free — watched
      </span>
    );
  }
  if (cost === "blocked") {
    return (
      <span className="chip warn" title="Full company data needs an active subscription">
        needs subscription
      </span>
    );
  }
  return (
    <span
      className="chip"
      title="Estimated — you confirm before any credit is drawn, and watched or showcase companies load free"
    >
      1 credit
    </span>
  );
}

// The cost line under the page title, keyed to the account's metering mode.
// `undefined` (context not loaded yet, or the load failed) gets a neutral,
// always-true sentence so a metered/locked user never flashes the coverage
// "watched companies are free" claim that is false for them.
function costHint(readModel: string | undefined): React.ReactNode {
  switch (readModel) {
    case "coverage":
      return "Viewing a company you don't watch uses one deep search credit; watched companies are always free to view.";
    case "metered":
      return "Viewing a company's full data uses one deep search credit.";
    case "locked":
      // Link Billing like the blocked/confirm states do — it's the primary
      // nudge for the one cohort that needs it.
      return (
        <>
          Full company data needs an active subscription — search stays free.
          Start a plan under <Link to="/billing">Billing</Link> to unlock it.
        </>
      );
    default:
      return "Search stays free; opening a company's full data may use a deep search credit, depending on your plan.";
  }
}

export function Companies() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CompanyHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(false);
  // The query string the current `results` are for — lets the UI show a
  // "press Enter to search" prompt once the field has diverged from it.
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  // Bumped on every search, edit and unmount; a late response only applies
  // if its seq still matches, so a superseded reply can't clobber the list.
  const searchSeq = useRef(0);
  // Read-model + watched set for the per-hit cost badges. One aggregate
  // call on mount (the same Postgres-only endpoint the Overview uses);
  // until it lands (or if it fails) hits simply render without a badge —
  // the confirm gate downstream is the binding cost statement either way.
  const [costCtx, setCostCtx] = useState<{
    readModel: string;
    watched: ReadonlySet<string>;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    // Retry a transient failure a couple of times with backoff: this endpoint
    // is rpm-capped (a burst can 429) and can 5xx on a cold worker, and the
    // cost badges are off for the whole session if the one mount-time fetch is
    // lost. On persistent failure costCtx stays null — the page still works
    // (search + the authoritative confirm gate), just without the badges.
    (async () => {
      for (let attempt = 0; attempt < 3 && alive; attempt++) {
        try {
          const ctx = await api.companyDetailContext();
          if (alive) {
            setCostCtx({
              readModel: ctx.read_model,
              watched: new Set(ctx.watched_ids),
            });
          }
          return;
        } catch {
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
          }
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Cancel any in-flight search on unmount so a late response can't
  // setState on an unmounted component (the seq guard no-ops it).
  useEffect(
    () => () => {
      searchSeq.current++;
    },
    [],
  );

  // Explicit fuzzy search — run on Enter or the Search button, NOT per
  // keystroke: the upstream scan can take a few seconds, so one search per
  // intent (same pattern as the Watchlists picker).
  async function runSearch() {
    const q = query.trim();
    if (q.length < 2 || searching) return;
    const seq = ++searchSeq.current;
    setSearching(true);
    setSearchErr(false);
    setSearchedQuery(q);
    try {
      const r = await api.searchCompanies(q);
      if (seq === searchSeq.current) setResults(r.hits);
    } catch {
      if (seq === searchSeq.current) {
        setResults([]);
        setSearchErr(true);
      }
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }

  return (
    <div className="main">
      <div className="page-header">
        <h1>Companies</h1>
      </div>
      <p className="page-sub">
        Search any company and open everything we hold on it — registry
        record, filed financials with history, implied valuation and funding
        rounds, share capital, charges, group structure, governance signals
        and the event timeline. {costHint(costCtx?.readModel)}
      </p>

      <div className="card">
        <div className="search-row" style={{ maxWidth: 560 }}>
          <input
            className="input"
            placeholder="Company name (e.g. Acme Holdings) or id (e.g. gb-coh-00006039)"
            value={query}
            aria-busy={searching}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              // Any edit makes the field the single source of truth: cancel
              // an in-flight search (seq bump, so its late response can't
              // land) and drop the now-stale results / error / spinner —
              // unless the trimmed value still matches what the current
              // results are for (e.g. only whitespace changed).
              if (v.trim() !== searchedQuery) {
                searchSeq.current++;
                setSearching(false);
                setSearchErr(false);
                setResults(null);
                setSearchedQuery(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runSearch();
              }
            }}
            autoFocus
          />
          <button
            type="button"
            className="btn secondary"
            onClick={runSearch}
            disabled={query.trim().length < 2 || searching}
          >
            {searching ? (
              <span className="btn-busy">
                <span className="spinner" aria-hidden /> Searching…
              </span>
            ) : (
              "Search"
            )}
          </button>
        </div>

        {/* Search is explicit — prompt to submit once the field holds
            something searchable that hasn't been searched yet. */}
        {!searching &&
          query.trim().length >= 2 &&
          query.trim() !== searchedQuery && (
            <p className="muted" style={{ marginTop: "var(--space-2)" }}>
              Press Enter or Search to look up “{query.trim()}”.
            </p>
          )}

        {/* Spoken counterpart to the spinner — announces the async search
            state to assistive tech. */}
        <span className="sr-only" role="status" aria-live="polite">
          {searching
            ? "Searching…"
            : searchErr
              ? "Search unavailable"
              : results
                ? `${results.length} ${results.length === 1 ? "match" : "matches"} found`
                : ""}
        </span>
        {searchErr && (
          <p className="muted" style={{ marginTop: "var(--space-2)" }}>
            Couldn't reach search just now — try again in a moment.
          </p>
        )}
        {results && !searching && results.length === 0 && !searchErr && (
          <p className="muted" style={{ marginTop: "var(--space-2)" }}>
            No matches — refine the name, or paste a namespaced id
            (gb-coh-…, ee-rik-…).
          </p>
        )}
        {/* First query in flight (no prior results): skeleton rows sketch
            the list so the panel resolves in place rather than popping in
            after a blank pause. */}
        {searching && !results && (
          <ul className="search-results" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li className="search-result" key={i}>
                <div style={{ flex: 1 }}>
                  <span className="skeleton" style={{ width: "45%" }} />
                  <span
                    className="skeleton"
                    style={{ width: "28%", marginTop: "var(--space-2)" }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        {results && results.length > 0 && (
          <ul
            className={`search-results${searching ? " is-refreshing" : ""}`}
          >
            {results.map((h) => (
              <li key={hitKey(h)} className="search-result">
                <div>
                  <div>{h.matched_name}</div>
                  <div className="muted mono" style={{ fontSize: 12 }}>
                    {h.entity_id}
                    {h.jurisdiction ? ` · ${h.jurisdiction}` : ""} ·{" "}
                    {Math.round(h.score * 100)}% match
                  </div>
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                  }}
                >
                  {costCtx && (
                    <HitCostBadge
                      cost={estimateHitCost(
                        costCtx.readModel,
                        costCtx.watched,
                        h.entity_id,
                      )}
                    />
                  )}
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() =>
                      navigate(`/companies/${encodeURIComponent(h.entity_id)}`)
                    }
                  >
                    View full data
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Direct-id affordance: a pasted namespaced id skips the search. */}
        {/^[a-z]{2}-[a-z0-9]+-[A-Za-z0-9]+$|^lei-[A-Za-z0-9]+$/.test(
          query.trim(),
        ) && (
          <p style={{ marginTop: "var(--space-3)" }}>
            <button
              type="button"
              className="btn"
              onClick={() =>
                navigate(`/companies/${encodeURIComponent(query.trim())}`)
              }
            >
              Open {query.trim()} →
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

// ─── detail sections ───────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const dead = /dissolv|liquidat|administrat|insolven/i.test(status);
  return (
    <span className={`pill ${dead ? "warn" : "ok"}`}>
      <span className="pill-dot" aria-hidden />
      {status}
    </span>
  );
}

function OverdueBadge() {
  return (
    <span
      className="chip warn"
      title="Estimated from the due date in our registry snapshot — the register may allow a grace period and our snapshot can lag, so the company may already have filed."
    >
      overdue (est.)
    </span>
  );
}

function DerivedTag() {
  return (
    <span
      className="chip"
      title="Filing-derived estimate reconstructed from statutory filings — probabilistic, indicative only, not advice."
    >
      derived · probabilistic
    </span>
  );
}

function SectionCard({
  title,
  tag,
  children,
}: {
  title: string;
  tag?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card detail-card">
      <div className="detail-card-head">
        <h2>{title}</h2>
        {tag}
      </div>
      {children}
    </section>
  );
}

function FinancialSummarySection({ s }: { s: FinancialSummaryDto }) {
  const cur = (v: number) => formatMoney(v, s.currency);
  const h = s.headline ?? {};
  const g = s.growth ?? {};
  const runway = s.runway ?? {};
  const ratios = s.ratios ?? {};
  return (
    <SectionCard
      title="Financial summary"
      tag={
        s.current_period_end && (
          <span className="muted" style={{ fontSize: 12 }}>
            period ending {fmtDate(s.current_period_end)}
          </span>
        )
      }
    >
      <div className="fig-grid">
        <Fig
          label="Turnover"
          field={h.turnover}
          fmt={cur}
          extra={<YoY growth={g.turnover_yoy} />}
        />
        <Fig label="Operating profit" field={h.operating_profit} fmt={cur} />
        <Fig label="Profit / loss" field={h.profit_loss} fmt={cur} />
        <Fig
          label="Equity"
          field={h.equity}
          fmt={cur}
          extra={<YoY growth={g.equity_yoy} />}
        />
        <Fig label="Cash" field={h.cash} fmt={cur} />
        <Fig
          label="Employees"
          field={h.employee_count}
          fmt={fmtCount}
          extra={<YoY growth={g.employee_count_yoy} />}
        />
        <Fig
          label="Monthly burn"
          field={runway.monthly_burn}
          fmt={cur}
        />
        <Fig
          label="Runway"
          field={runway.runway_months}
          fmt={(v) => `${Math.round(v)} mo`}
        />
        <Fig
          label="Directors' pay / turnover"
          field={ratios.directors_remuneration_ratio}
          fmt={(v) => fmtPct(v, false)}
        />
      </div>
      <div className="chip-row">
        {s.profitability_flag != null && (
          <span className="chip">
            {s.profitability_flag ? "Operating-profitable" : "Operating loss"}
          </span>
        )}
        {s.parse_confidence && (
          <span className="chip">confidence: {s.parse_confidence}</span>
        )}
        {s.prior_period_end && (
          <span className="chip">
            YoY vs year ending {fmtDate(s.prior_period_end)}
          </span>
        )}
      </div>
    </SectionCard>
  );
}

// Multi-period accounts table. Rows are dropped when no period carries the
// concept, so a micro-entity's table stays tight.
function AccountsHistorySection({
  periods,
  coverage,
}: {
  periods: AccountsPeriodDto[];
  coverage:
    | {
        period_count: number;
        earliest_period: string | null;
        latest_period: string | null;
      }
    | undefined;
}) {
  const rows: [string, (p: AccountsPeriodDto) => FieldEnvelope | undefined][] =
    [
      ["Turnover", (p) => p.profit_and_loss?.turnover],
      ["Gross profit", (p) => p.profit_and_loss?.gross_profit],
      ["Operating profit", (p) => p.profit_and_loss?.operating_profit],
      ["Profit / loss", (p) => p.profit_and_loss?.profit_loss],
      ["Equity", (p) => p.balance_sheet?.equity],
      ["Total assets", (p) => p.balance_sheet?.total_assets],
      ["Cash", (p) => p.balance_sheet?.cash],
      ["Creditors", (p) => p.balance_sheet?.creditors],
      ["Employees", (p) => p.notes?.employee_count],
    ];
  const live = rows.filter(([, pick]) =>
    periods.some((p) => {
      const f = pick(p);
      return f && f.value != null && f.source !== "unavailable";
    }),
  );
  if (!live.length) return null;
  return (
    <SectionCard
      title="Accounts history"
      tag={
        coverage &&
        coverage.period_count > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {fmtCount(coverage.period_count)} periods on file
            {coverage.earliest_period && coverage.latest_period
              ? ` · ${fmtDate(coverage.earliest_period)} – ${fmtDate(coverage.latest_period)}`
              : ""}
          </span>
        )
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Period ending</th>
              {periods.map((p, i) => (
                <th className="num" key={p.period_end ?? i}>
                  {p.period_end ? fmtDate(p.period_end) : "—"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {live.map(([label, pick]) => (
              <tr key={label}>
                <td>{label}</td>
                {periods.map((p, i) => {
                  const f = pick(p);
                  const has =
                    f && f.value != null && f.source !== "unavailable";
                  const fmt =
                    label === "Employees"
                      ? fmtCount
                      : (v: number) => formatMoney(v, p.currency);
                  return (
                    <td className="num" key={p.period_end ?? i}>
                      {has ? (
                        <>
                          {fmt(f.value as number)}
                          <SourceFlag source={f.source} />
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted detail-note">
        As filed, per reporting period (newest first). The full time series
        and per-period balance sheets are also available over the API.
      </p>
    </SectionCard>
  );
}

// A derived valuation/round that is a filing ARTEFACT, not a real market
// round: the engine tags token-share holdco/SPV injections
// `single_share_injection` (valuation.py::_apply_injection_cap) — e.g. a
// 1-share £16M injection whose naive post-money is spurious (Beavertown's
// £1.6bn). Mirrors site/src/landing/seo.ts::isValuationArtefact; keyed on the
// REASON, not the `low` tier, so a genuine unsized low-confidence round isn't
// swept up. Kept as the same literal the Python producer + site consumer use.
const INJECTION_REASON = "single_share_injection";
function isInjectionArtefact(v: {
  confidence_reasons?: string[] | null;
}): boolean {
  return !!v.confidence_reasons?.includes(INJECTION_REASON);
}

function ValuationSection({ v }: { v: ValuationDto }) {
  const noFigures =
    v.post_money == null && v.pre_money == null && v.price_per_share == null;
  // Never HEADLINE a low-confidence implied valuation. The pipeline forces
  // token-share injection artefacts to confidence 'low' (spurious huge
  // post-moneys), so suppressing 'low' keeps an artefact number from being
  // presented as an authoritative figure — CLAUDE.md invariant 4, and the
  // same treatment the public renderer applies (render.ts drops
  // confidence === 'low'). The underlying allotments still show in the
  // funding-round history below when they're genuine.
  if (noFigures || v.confidence === "low") {
    const reason = noFigures
      ? v.parse_status === "no_sh01_filings"
        ? "No share-allotment (SH01) filings on record, so there's no priced funding round to derive a valuation from."
        : v.parse_status === "no_priced_round_found"
          ? "Share allotments are on record, but none were priced (e.g. nominal-value issues or option grants), so there's no round to value."
          : v.parse_status === "sh01_documents_unreadable"
            ? "The company's share-allotment (SH01) filings are scanned paper documents we couldn't read, so any priced round in them isn't captured — a coverage gap, not a company that never raised."
            : "No priced funding round found to derive a valuation from."
      : "The most recent priced round isn't a reliable basis for a headline valuation — its confidence is low (e.g. a token-share injection or an unsized reconstruction), so the figures aren't shown here.";
    return (
      <SectionCard title="Implied valuation" tag={<DerivedTag />}>
        <p className="muted" style={{ margin: 0 }}>
          {reason}
          {noFigures &&
            v.parse_status !== "sh01_documents_unreadable" &&
            " That's what the filings show — not a valuation of zero."}
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Latest implied valuation" tag={<DerivedTag />}>
      <div className="fig-grid">
        <Fig
          label="Post-money"
          field={{ value: v.post_money, source: "derived" }}
          fmt={(x) => formatMoney(x, v.currency)}
        />
        <Fig
          label="Pre-money"
          field={{ value: v.pre_money, source: "derived" }}
          fmt={(x) => formatMoney(x, v.currency)}
        />
        <Fig
          label="Price / share"
          field={{ value: v.price_per_share, source: "derived" }}
          fmt={(x) => fmtMoneyExact(x, v.currency, 4)}
        />
      </div>
      <div className="chip-row">
        {v.confidence && <span className="chip">confidence: {v.confidence}</span>}
        {v.valuation_basis === "accounts_share_issuance"
          ? v.period_end && (
              <span className="chip">from the {fmtDate(v.period_end)} accounts</span>
            )
          : v.as_of_sh01_date && (
              <span className="chip">
                from the SH01 filed {fmtDate(v.as_of_sh01_date)}
              </span>
            )}
        {(v.confidence_reasons ?? []).map((r) => (
          <span className="chip" key={r}>
            {humaniseSlug(r).toLowerCase()}
          </span>
        ))}
      </div>
      <p className="muted detail-note">
        {v.valuation_basis === "accounts_share_issuance"
          ? "Implied from the statutory accounts' share-premium movement, sized against the latest cap table — a filing-derived estimate, not an appraisal or advice."
          : "Reconstructed from the latest priced share allotment against the cap table — a filing-derived estimate, not an appraisal or advice."}
      </p>
    </SectionCard>
  );
}

function FundingRoundsSection({ fr }: { fr: FundingRoundsDto }) {
  // Drop token-share injection artefacts (spurious figures) as the public
  // page does; keep genuine low-confidence rounds (each row shows its own
  // confidence). If nothing survives, render nothing.
  const rounds = (fr.rounds ?? []).filter((r) => !isInjectionArtefact(r));
  if (!rounds.length) return null;
  return (
    <SectionCard title="Funding rounds" tag={<DerivedTag />}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Allotment date</th>
              <th>Share class</th>
              <th className="num">Shares</th>
              <th className="num">Price / share</th>
              <th className="num">Pre-money</th>
              <th className="num">Post-money</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rounds.map((r, i) => {
              const cur = r.currency ?? fr.currency ?? null;
              return (
                <tr key={`${r.allotment_date}-${i}`}>
                  <td>{fmtDate(r.allotment_date)}</td>
                  <td>{r.share_class ?? "—"}</td>
                  <td className="num">{fmtCount(r.shares_allotted)}</td>
                  <td className="num">
                    {fmtMoneyExact(r.price_per_share, cur, 4)}
                  </td>
                  <td className="num">
                    {r.pre_money != null ? formatMoney(r.pre_money, cur) : "—"}
                  </td>
                  <td className="num">
                    {r.post_money != null
                      ? formatMoney(r.post_money, cur)
                      : "—"}
                  </td>
                  <td>{r.confidence ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted detail-note">
        Priced rounds reconstructed from SH01 share-allotment filings —
        filing-derived estimates, not advice.
      </p>
    </SectionCard>
  );
}

function ShareCapitalSection({ sc }: { sc: ShareCapitalDto }) {
  if (!sc.share_classes?.length) {
    return (
      <SectionCard title="Share capital">
        <p className="muted" style={{ margin: 0 }}>
          No parseable statement of capital on the latest confirmation
          statement ({humaniseSlug(sc.parse_status).toLowerCase()}).
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Share capital"
      tag={
        sc.statement_of_capital_date && (
          <span className="muted" style={{ fontSize: 12 }}>
            statement made up to {fmtDate(sc.statement_of_capital_date)}
          </span>
        )
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Share class</th>
              <th className="num">Shares issued</th>
              <th className="num">Aggregate nominal</th>
              <th className="num">Paid up / share</th>
            </tr>
          </thead>
          <tbody>
            {sc.share_classes.map((c, i) => (
              <tr key={`${c.share_class}-${i}`}>
                <td>{c.share_class ?? "—"}</td>
                <td className="num">{fmtCount(c.issued_count)}</td>
                <td className="num">
                  {fmtMoneyExact(c.aggregate_nominal, c.currency ?? sc.currency)}
                </td>
                <td className="num">
                  {fmtMoneyExact(
                    c.paid_up_per_share,
                    c.currency ?? sc.currency,
                    6,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="chip-row">
        {sc.total_issued_shares != null && (
          <span className="chip">
            {fmtCount(sc.total_issued_shares)} shares in total
          </span>
        )}
        {sc.parse_confidence && (
          <span className="chip">
            parsed from the CS01 PDF · confidence: {sc.parse_confidence}
          </span>
        )}
      </div>
    </SectionCard>
  );
}

function ChargesSection({ c }: { c: ChargesDto }) {
  if (c.parse_status === "upstream_unavailable") {
    return (
      <SectionCard title="Charges (secured debt)">
        <p className="muted" style={{ margin: 0 }}>
          Charge register temporarily unavailable — not "no debt". Try again
          shortly.
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Charges (secured debt)"
      tag={
        <span className="muted" style={{ fontSize: 12 }}>
          {fmtCount(c.outstanding_count)} outstanding ·{" "}
          {fmtCount(c.satisfied_count)} satisfied · {fmtCount(c.total_count)}{" "}
          total
        </span>
      }
    >
      {c.items?.length ? (
        <ul className="detail-list">
          {c.items.map((it, i) => (
            <li key={it.charge_id ?? i}>
              <span
                className={`chip${it.status === "outstanding" ? " warn" : ""}`}
              >
                {it.status}
              </span>{" "}
              {it.persons_entitled?.length ? (
                <strong>{it.persons_entitled.join(", ")}</strong>
              ) : (
                <span className="muted">creditor not stated</span>
              )}
              <span className="muted">
                {it.created_on ? ` · created ${fmtDate(it.created_on)}` : ""}
                {it.satisfied_on
                  ? ` · satisfied ${fmtDate(it.satisfied_on)}`
                  : ""}
                {it.classification
                  ? ` · ${humaniseSlug(it.classification).toLowerCase()}`
                  : ""}
                {it.is_floating_charge ? " · floating charge" : ""}
              </span>
              {it.secured_description && (
                <div className="muted" style={{ fontSize: 12 }}>
                  {it.secured_description}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          No charges on the register.
        </p>
      )}
    </SectionCard>
  );
}

function ConsolidationSection({ c }: { c: ConsolidationDto }) {
  const parents = c.parents ?? [];
  const subs = c.subsidiaries ?? [];
  if (c.parse_status !== "ok" || (!parents.length && !subs.length)) {
    return null;
  }
  const row = (it: ConsolidationDto["parents"][number]) => {
    // Link to the internal detail page for any namespaced id — including
    // lei-* issuers, which the detail view serves but which have no public
    // landing page (so companyUrl would have wrongly dropped them).
    const to = companyDetailPath(it.company_id);
    const name = it.name ?? it.lei;
    return (
      <li key={`${it.company_id}-${it.relationship}`}>
        {to ? <Link to={to}>{name}</Link> : <strong>{name}</strong>}
        {it.jurisdiction && (
          <span className="chip" style={{ marginLeft: "var(--space-2)" }}>
            {it.jurisdiction.toUpperCase()}
          </span>
        )}
        <span className="muted">
          {" "}
          · {it.relationship}
          {it.start_date ? ` since ${fmtDate(it.start_date)}` : ""}
        </span>
      </li>
    );
  };
  return (
    <SectionCard
      title="Group structure"
      tag={
        <span className="muted" style={{ fontSize: 12 }}>
          {c.source}
          {c.as_of ? ` · as of ${fmtDate(c.as_of)}` : ""}
        </span>
      }
    >
      {parents.length > 0 && (
        <>
          <div className="eyebrow">Consolidated by</div>
          <ul className="detail-list">{parents.map(row)}</ul>
        </>
      )}
      {subs.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: "var(--space-3)" }}>
            Consolidates
          </div>
          <ul className="detail-list">{subs.map(row)}</ul>
        </>
      )}
    </SectionCard>
  );
}

function envelope(v: unknown): FieldEnvelope | undefined {
  if (v && typeof v === "object" && "value" in (v as object)) {
    return v as FieldEnvelope;
  }
  return undefined;
}

function GovernanceSection({ g }: { g: GovernanceDto }) {
  const score = g.hygiene_score;
  return (
    <SectionCard title="Governance signals" tag={<DerivedTag />}>
      <div className="fig-grid">
        <div className="fig">
          <div className="eyebrow">Hygiene score</div>
          <div className="fig-value mono">
            {score != null ? (
              <>
                {score}
                <span className="muted"> / 100</span>
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
        <Fig
          label="Late filings (3y)"
          field={envelope(g.signals?.late_filing_count_3y)}
          fmt={fmtCount}
        />
        <Fig
          label="Auditor changes (3y)"
          field={envelope(g.signals?.auditor_changes_3y)}
          fmt={fmtCount}
        />
        <Fig
          label="Outstanding charges"
          field={envelope(g.signals?.outstanding_charges_count)}
          fmt={fmtCount}
        />
      </div>
      <p className="muted detail-note">
        The score is an interpretive composite over countable registry facts
        — indicative, not a compliance rating.
      </p>
    </SectionCard>
  );
}

function EventsSection({ ev }: { ev: EventsDto }) {
  if (!ev.events?.length) {
    return (
      <SectionCard title="Event timeline">
        <p className="muted" style={{ margin: 0 }}>
          {ev.parse_status === "degraded"
            ? "Timeline temporarily degraded — an empty list here doesn't mean a quiet company."
            : "No events recorded yet."}
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Event timeline"
      tag={
        <span className="muted" style={{ fontSize: 12 }}>
          latest {ev.events.length}
          {ev.count > ev.events.length ? ` of ${fmtCount(ev.count)}` : ""}
        </span>
      }
    >
      <div>
        {ev.events.map((e, i) => (
          <div
            key={e.event_id ?? i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              padding: "var(--space-2) 0",
              borderTop:
                i === 0 ? "none" : "1px solid var(--color-border-subtle)",
            }}
          >
            <span
              className={`status-dot${isDistressKind(e.event_type) ? " risk" : ""}`}
              aria-hidden
            />
            <span style={{ flex: "1 1 auto" }}>
              <strong>{EVENT_LABELS[e.event_type] ?? e.event_type}</strong>
              {e.confidence && e.confidence !== "high" && (
                <span className="muted" style={{ fontSize: 12 }}>
                  {" "}
                  · {e.confidence} confidence
                </span>
              )}
            </span>
            <span className="muted mono" style={{ fontSize: 12 }}>
              {fmtDate(e.event_date)}
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function FilingsSection({ f }: { f: FilingsDto }) {
  if (!f.items?.length) return null;
  return (
    <SectionCard
      title="Recent filings"
      tag={
        f.total_count != null && (
          <span className="muted" style={{ fontSize: 12 }}>
            latest {f.items.length} of {fmtCount(f.total_count)}
          </span>
        )
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Category</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {f.items.map((it, i) => (
              <tr key={it.transaction_id ?? i}>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(it.date)}</td>
                <td className="mono">{it.type ?? "—"}</td>
                <td>{it.category ?? "—"}</td>
                <td>{humaniseSlug(it.description)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ─── detail route ──────────────────────────────────────────────────────

type DetailState =
  | { kind: "quoting" }
  | { kind: "confirm"; quote: DetailQuote }
  | { kind: "blocked" }
  | { kind: "loading" }
  | { kind: "ok"; data: CompanyDetail }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

export function CompanyDetailPage() {
  const { companyId = "" } = useParams();
  const [state, setState] = useState<DetailState>({ kind: "quoting" });
  // Monotonic view token: navigating to another company (or unmounting)
  // bumps it, so a slow in-flight quote/detail response for the PREVIOUS
  // company can never clobber the current view.
  const viewSeq = useRef(0);

  function load() {
    const seq = viewSeq.current;
    const setIfCurrent = (s: DetailState) => {
      if (viewSeq.current === seq) setState(s);
    };
    setState({ kind: "loading" });
    api
      .companyDetail(companyId)
      .then((data) => setIfCurrent({ kind: "ok", data }))
      .catch((e) => {
        const msg = (e as Error).message;
        if (msg === "company_not_found") setIfCurrent({ kind: "not_found" });
        else if (msg === "subscription_required") {
          setIfCurrent({ kind: "blocked" });
        } else if (msg === "quota_exceeded") {
          setIfCurrent({
            kind: "error",
            message:
              "You've used all this month's deep search credits — credits reset with your billing month, or upgrade under Billing.",
          });
        } else if (msg === "rate_limited") {
          setIfCurrent({
            kind: "error",
            message: "Too many requests — give it a few seconds and retry.",
          });
        } else {
          setIfCurrent({
            kind: "error",
            message:
              "Couldn't load the company data just now — the data tier may be waking up. Try again in a moment.",
          });
        }
      });
  }

  // Quote first: free views (watched / showcase) load straight away; a
  // view that would draw a credit asks first, so a click is never a
  // surprise charge.
  useEffect(() => {
    viewSeq.current++;
    const seq = viewSeq.current;
    setState({ kind: "quoting" });
    api
      .companyDetailQuote(companyId)
      .then((quote) => {
        if (viewSeq.current !== seq) return;
        if (quote.blocked) setState({ kind: "blocked" });
        else if (quote.unit_cost === 0) load();
        else setState({ kind: "confirm", quote });
      })
      .catch(() => {
        if (viewSeq.current === seq) {
          setState({
            kind: "error",
            message: "Couldn't check this view's cost — try again in a moment.",
          });
        }
      });
    return () => {
      // Unmount / company switch: invalidate anything still in flight.
      viewSeq.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="main">
      <p style={{ marginBottom: "var(--space-3)" }}>
        <Link to="/companies" className="section-link">
          ← Company search
        </Link>
      </p>

      {state.kind === "quoting" && (
        <p className="muted">Checking availability…</p>
      )}

      {state.kind === "confirm" && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>View all data for this company?</h2>
          {/* A metered (developer) plan charges every deep read regardless of
              watch state, so the watchlist framing below would be false for
              it — state the cost plainly instead. */}
          {state.quote.read_model === "metered" ? (
            <p className="muted">
              Opening the full record for{" "}
              <span className="mono">{state.quote.company_id}</span> uses{" "}
              <strong>1 deep search credit</strong> from your plan's included
              units.
            </p>
          ) : (
            <p className="muted">
              <span className="mono">{state.quote.company_id}</span> isn't on
              your watchlist, so opening its full record uses{" "}
              <strong>1 deep search credit</strong> from your plan's included
              units. Companies you watch are always free to view.
            </p>
          )}
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <button type="button" className="btn primary" onClick={load}>
              View company data — use 1 credit
            </button>
            {/* Watching only makes future views free on coverage plans, so
                omit the "watch instead" nudge for metered plans. */}
            {state.quote.read_model !== "metered" && (
              <Link to="/watchlists" className="btn secondary">
                Watch it instead (free views)
              </Link>
            )}
          </div>
        </div>
      )}

      {state.kind === "blocked" && (
        <div className="callout warn">
          <span>
            Full company data needs an active subscription — search and
            watchlists stay open. Start a plan under{" "}
            <Link to="/billing">Billing</Link> to unlock deep reads.
          </span>
        </div>
      )}

      {state.kind === "loading" && (
        <div role="status">
          <span className="sr-only">Loading company data…</span>
          <div className="card" aria-hidden>
            {["55%", "70%", "45%"].map((w, i) => (
              <span
                className="skeleton"
                key={i}
                style={{ width: w, marginTop: i === 0 ? 0 : "var(--space-4)" }}
              />
            ))}
          </div>
          <p className="muted">
            Pulling everything we hold — registry record, financial history,
            capital events, charges, governance…
          </p>
        </div>
      )}

      {state.kind === "not_found" && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Company not found</h2>
          <p className="muted">
            <span className="mono">{companyId}</span> isn't in the registry
            snapshot. Check the id, or search by name instead.
          </p>
        </div>
      )}

      {state.kind === "error" && <p className="error">{state.message}</p>}

      {state.kind === "ok" && <CompanyDetailView data={state.data} />}
    </div>
  );
}

function CompanyDetailView({ data }: { data: CompanyDetail }) {
  const p = data.profile;
  const publicHref = companyUrl(data.company_id);
  const history = data.accounts_history;
  const periods = history?.periods ?? [];

  const registryRows: [string, React.ReactNode][] = [];
  if (p.sic_codes?.length) {
    registryRows.push([
      "Industry (SIC)",
      <span className="chip-row" style={{ marginTop: 0 }} key="sic">
        {p.sic_codes.map((raw) => {
          const m = raw.match(/^(\d{4,5})\s*[-–]\s*(.+)$/);
          return (
            <span className="chip" key={raw} title={m ? `SIC ${m[1]}` : undefined}>
              {m ? m[2] : raw}
            </span>
          );
        })}
      </span>,
    ]);
  }
  if (p.accounts_category) {
    registryRows.push(["Accounts type", p.accounts_category]);
  }
  if (p.accounts_last_made_up) {
    registryRows.push(["Accounts last made up", fmtDate(p.accounts_last_made_up)]);
  }
  if (p.accounts_next_due) {
    registryRows.push([
      "Accounts next due",
      <span key="and">
        {fmtDate(p.accounts_next_due)}{" "}
        {isOverdue(p.accounts_next_due) && <OverdueBadge />}
      </span>,
    ]);
  }
  if (p.confirmation_statement_last) {
    registryRows.push([
      "Confirmation statement filed",
      fmtDate(p.confirmation_statement_last),
    ]);
  }
  if (p.confirmation_statement_next_due) {
    registryRows.push([
      "Confirmation statement due",
      <span key="csd">
        {fmtDate(p.confirmation_statement_next_due)}{" "}
        {isOverdue(p.confirmation_statement_next_due) && <OverdueBadge />}
      </span>,
    ]);
  }
  if (p.registered_office) {
    registryRows.push(["Registered office", p.registered_office]);
  }
  if (p.previous_names?.length) {
    registryRows.push(["Previous names", p.previous_names.join(" · ")]);
  }

  const freshness =
    p.monitored && p.last_changed
      ? `Live-synced with the register · last change ${fmtDate(p.last_changed)}`
      : p.snapshot_date
        ? `Registry data as of ${fmtDate(p.snapshot_date)}`
        : null;

  return (
    <>
      <div className="page-header">
        <h1>{p.name ?? data.company_id}</h1>
        <StatusBadge status={p.status} />
      </div>
      <div className="entity-meta" style={{ marginBottom: "var(--space-2)" }}>
        <span className="mono">{data.company_id}</span>
        {p.registration_number && <span>No. {p.registration_number}</span>}
        {p.legal_form && <span>{p.legal_form}</span>}
        {p.incorporation_date && (
          <span>Incorporated {fmtDate(p.incorporation_date)}</span>
        )}
        {p.dissolution_date && (
          <span>Dissolved {fmtDate(p.dissolution_date)}</span>
        )}
        {publicHref && (
          <a href={publicHref} target="_blank" rel="noreferrer">
            Public page ↗
          </a>
        )}
      </div>
      <div className="chip-row" style={{ marginBottom: "var(--space-4)" }}>
        {data.charged_units > 0 ? (
          <span className="chip">1 deep search credit used</span>
        ) : (
          <span className="chip ok">
            {data.free_reason === "watched"
              ? "Free view — on your watchlist"
              : data.free_reason === "showcase"
                ? "Free view — showcase company"
                : "Free view"}
          </span>
        )}
        {freshness && <span className="chip">{freshness}</span>}
        {data.free_reason !== "watched" && (
          <Link to="/watchlists" className="chip" style={{ textDecoration: "none" }}>
            Watch this company → free views + event alerts
          </Link>
        )}
      </div>

      {registryRows.length > 0 && (
        <SectionCard title="Registry record">
          <dl className="detail-dl">
            {registryRows.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      )}

      {data.financial_summary && (
        <FinancialSummarySection s={data.financial_summary} />
      )}
      {periods.length > 0 && (
        <AccountsHistorySection periods={periods} coverage={history?.coverage} />
      )}
      {data.latest_valuation && <ValuationSection v={data.latest_valuation} />}
      {data.funding_rounds && <FundingRoundsSection fr={data.funding_rounds} />}
      {data.share_capital && <ShareCapitalSection sc={data.share_capital} />}
      {data.charges && <ChargesSection c={data.charges} />}
      {data.consolidation && <ConsolidationSection c={data.consolidation} />}
      {data.governance_signals && (
        <GovernanceSection g={data.governance_signals} />
      )}
      {data.events && <EventsSection ev={data.events} />}
      {data.filings && <FilingsSection f={data.filings} />}

      <p className="muted detail-note" style={{ marginTop: "var(--space-5)" }}>
        Source: statutory filings and public registers (as-reported).
        Financial figures are for the accounting periods shown; derived
        valuations, funding rounds and governance scores are probabilistic
        filing-derived estimates — indicative, not advice. Fetched{" "}
        {fmtDate(data.fetched_at)}.
      </p>
    </>
  );
}
